import type { ReactNode } from "react";

import { Button } from "@bindery/ui/components/ui/button";

/** Matches `TokenSession["kind"]` from `@bindery/auth-consumer/react`. */
export type SessionGateStatus =
  | "loading"
  | "signed-out"
  | "unreachable"
  | "ready";

export interface SessionGateProps {
  status: SessionGateStatus;
  onSignIn: () => void;
  /** What the default signed-out notice says above its sign-in button. */
  signedOutMessage?: ReactNode;
  /** Replaces the default signed-out notice. */
  signedOut?: ReactNode;
  loading?: ReactNode;
  /** Replaces the default unreachable notice. */
  unreachable?: ReactNode;
  /** The unreachable notice's retry; reloads the page by default. */
  onRetry?: () => void;
  children: ReactNode;
}

export function SessionGate({
  status,
  onSignIn,
  signedOutMessage = "Sign in to continue.",
  signedOut,
  loading,
  unreachable,
  onRetry = () => window.location.reload(),
  children,
}: SessionGateProps) {
  switch (status) {
    case "loading":
      return (
        loading ?? (
          <p className="text-muted-foreground text-sm">
            Checking your sign-in…
          </p>
        )
      );
    case "signed-out":
      return (
        signedOut ?? (
          <SessionNotice
            message={signedOutMessage}
            action="Sign in"
            onAction={onSignIn}
          />
        )
      );
    case "unreachable":
      return (
        unreachable ?? (
          <SessionNotice
            message="Your sign-in could not be checked because the sign-in service did not answer. Try again in a moment."
            action="Try again"
            onAction={onRetry}
          />
        )
      );
    case "ready":
      return children;
  }
}

function SessionNotice({
  message,
  action,
  onAction,
}: {
  message: ReactNode;
  action: string;
  onAction: () => void;
}) {
  return (
    <div className="bg-muted/40 flex min-h-48 flex-col items-center justify-center gap-4 rounded-lg border border-dashed px-6 py-10 text-center">
      <p className="text-muted-foreground text-sm">{message}</p>
      <Button onClick={onAction}>{action}</Button>
    </div>
  );
}
